// BuildPilot analytics — a thin, privacy-safe wrapper over GTM's dataLayer.
//
// GA4 is delivered EXCLUSIVELY through Google Tag Manager (GTM-KZXCD7ZS); this
// module never loads gtag.js. It only pushes custom events + Consent Mode
// updates. It deliberately NEVER sends names, emails, phone numbers,
// addresses or free-text queries to the dataLayer — only non-identifying
// dimensions (project type, state, category, counts) and a random event_id
// for conversion de-duplication.

const hasWindow = () => typeof window !== 'undefined';

export function pushDataLayer(obj) {
  if (!hasWindow()) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(obj);
}

// Drop null/undefined/'' so events stay clean and predictable in GTM.
function clean(params = {}) {
  const out = {};
  Object.keys(params).forEach((k) => {
    const v = params[k];
    if (v !== undefined && v !== null && v !== '') out[k] = v;
  });
  return out;
}

export function trackEvent(event, params = {}) {
  pushDataLayer({ event, ...clean(params) });
}

// Non-identifying id used to de-duplicate/reconcile conversions.
export function newEventId() {
  try {
    if (hasWindow() && window.crypto && window.crypto.randomUUID) {
      return window.crypto.randomUUID();
    }
  } catch (e) { /* noop */ }
  return 'e_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
}

// ─── Consent Mode v2 updates (driven by the cookie banner) ───
function updateConsent(granted) {
  if (!hasWindow()) return;
  const state = granted ? 'granted' : 'denied';
  const payload = {
    ad_storage: state,
    ad_user_data: state,
    ad_personalization: state,
    analytics_storage: state,
  };
  // Prefer the gtag shim defined in index.html; fall back to a raw push.
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', payload);
  } else {
    pushDataLayer(['consent', 'update', payload]);
  }
}
export const grantConsent = () => updateConsent(true);
export const denyConsent = () => updateConsent(false);

// ─── BuildPilot conversion events ───
// Stable de-dup: when a non-personal backend/transaction id is supplied we
// reuse it as the event_id and fire at most once per id, so retries or
// repeated success callbacks of the SAME submission can't create separate
// conversion ids. Without an id we fall back to a random event_id.
const _firedConversions = new Set();
export function trackConversionOnce(event, params = {}, dedupeId) {
  const id = dedupeId || newEventId();
  if (dedupeId) {
    const key = event + ':' + dedupeId;
    if (_firedConversions.has(key)) return false;
    _firedConversions.add(key);
  }
  trackEvent(event, { ...params, event_id: id });
  return true;
}

// De-dup for builder_search. Each genuine search is a distinct NAVIGATION to a
// results page, which React Router stamps with a unique, refresh-stable
// location.key. We de-dup on a composite token of that key + a lightweight hash
// of the query, remembering the SET of tokens already counted this session, so:
//   • every distinct search navigation fires exactly ONCE,
//   • a browser REFRESH reuses the same history entry (same key) → not recounted,
//   • a BACK/FORWARD revisit restores the original entry's key → not recounted,
//   • a React re-render never changes the token → not recounted,
//   • a DELIBERATE new search — even for the SAME term — is a brand-new
//     navigation with a fresh key → counted again (genuine repeat).
// Entries with no real key (direct links / "see all results" full loads, where
// key === 'default') fall back to the query hash so different queries are still
// separated. The token lives only in the visitor's own sessionStorage and is
// NEVER sent to GA4 — only the non-identifying category + count are pushed.
function _hashQuery(q) {
  const s = String(q || '').trim().toLowerCase();
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return 's' + h.toString(36);
}
const _SEARCH_DEDUP_KEY = 'bp:bs_fired';
function _searchAlreadyFired(token) {
  try {
    if (!hasWindow()) return false;
    const raw = window.sessionStorage.getItem(_SEARCH_DEDUP_KEY);
    const fired = raw ? JSON.parse(raw) : [];
    if (fired.includes(token)) return true; // refresh / back-revisit / rerender
    fired.push(token);
    // Cap growth over very long sessions; keep the most recent tokens.
    const capped = fired.slice(-50);
    window.sessionStorage.setItem(_SEARCH_DEDUP_KEY, JSON.stringify(capped));
    return false;
  } catch (e) {
    return false; // storage blocked / parse error → fail open (fire, never break)
  }
}

export function trackBuilderSearch({ searchCategory, resultCount, navKey, dedupeQuery } = {}) {
  if (dedupeQuery !== undefined && dedupeQuery !== null) {
    const token = (navKey || 'default') + ':' + _hashQuery(dedupeQuery);
    if (_searchAlreadyFired(token)) return false;
  }
  trackEvent('builder_search', {
    search_category: searchCategory,
    result_count: typeof resultCount === 'number' ? resultCount : undefined,
  });
  return true;
}

export function trackBuilderMatchComplete({ projectType, state, builderCount, dedupeId } = {}) {
  trackConversionOnce('builder_match_complete', {
    project_type: projectType,
    state,
    builder_count: typeof builderCount === 'number' ? builderCount : undefined,
  }, dedupeId);
}

export function trackQuoteRequest({ projectType, state, searchCategory, dedupeId } = {}) {
  trackConversionOnce('quote_request_submit', {
    project_type: projectType,
    state,
    search_category: searchCategory,
  }, dedupeId);
}

export function trackGenerateLead({ category, state, acquisitionSource, dedupeId } = {}) {
  trackConversionOnce('generate_lead', {
    lead_category: category,
    state,
    acquisition_source: acquisitionSource,
  }, dedupeId);
}

// Global, de-duplicated click tracking for App Store / Google Play links.
// One delegated listener covers every current and future download link
// site-wide, so individual components don't each need wiring.
let _downloadBound = false;
export function initAppDownloadTracking() {
  if (!hasWindow() || _downloadBound) return;
  _downloadBound = true;
  document.addEventListener('click', (e) => {
    try {
      const a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
      if (!a) return;
      const href = a.getAttribute('href') || '';
      let store = null;
      if (/apps\.apple\.com/i.test(href)) store = 'app_store';
      else if (/play\.google\.com/i.test(href)) store = 'google_play';
      if (!store) return;
      trackEvent('app_download_click', {
        store,
        link_location: window.location.pathname,
      });
    } catch (err) { /* never break a click */ }
  }, { capture: true });
}
